// Sugalarm — typed fetch wrapper.
//
// Injects `Authorization: Bearer <token>` from auth.ts. On 401, tries one
// refresh against /api/auth/refresh (only if the active bearer is a JWT,
// not a device token — device tokens have no refresh path) and retries
// the original request once. Returns ApiResult so callers don't `try`.

import { apiUrl } from "../env";
import {
  clearAll,
  getBearer,
  getDeviceToken,
  getJwt,
  getOrCreateClientId,
  setJwt,
} from "./auth";
// Cycle 433: the coalesced-refresh counter rides the activation report's
// transport note. `ws.ts` imports nothing from here, so this is not a cycle.
import {
  noteRefreshCoalesced,
  noteRefreshStart,
  noteRefreshTimeout,
} from "./ws";

/** Cycle 429: every request names the install it came from, so the
 *  server's custody-door log line can say WHICH phone tapped. The value is
 *  the same `client_id` the activation report posts — opaque, stable per
 *  install, never a token, never a name. Best-effort: storage that throws
 *  simply sends no header. */
export const CLIENT_ID_HEADER = "X-Sugalarm-Client-Id";

function clientIdHeader(): Record<string, string> {
  let out: Record<string, string> = {};
  try {
    const id = getOrCreateClientId();
    out = id ? { [CLIENT_ID_HEADER]: id } : {};
  } catch {
    out = {};
  }
  // Cycle 456: the zone this device's clock renders in, so the custody
  // notice can say "Treatment timer ends 11:36" in household time. The
  // server validates it through `zoneinfo` and uses it for that one clock
  // time only. Best-effort: no Intl, no header.
  const tz = deviceTimeZone();
  if (tz) out[TIMEZONE_HEADER] = tz;
  return out;
}

/** Cycle 456: the header the custody opener snapshots the zone from. */
export const TIMEZONE_HEADER = "X-Sugalarm-Timezone";

export function deviceTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === "string" && /^[A-Za-z][A-Za-z0-9_+\-/]{0,63}$/.test(tz)
      ? tz
      : null;
  } catch {
    return null;
  }
}
import type { ApiError, ApiErrorKind, ApiResult } from "./types";

interface RequestOpts {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip the bearer header (used by /api/auth/login, /api/pairing/request, etc.). */
  noAuth?: boolean;
  /** When true, do not attempt the 401 refresh dance. */
  noRetry?: boolean;
  /** Force a different bearer for this one call (used by /api/auth/refresh). */
  bearer?: string | null;
  /** Cycle 433 — abort this request. iOS freezes a fetch mid-flight when the
   *  app backgrounds and the promise can pend for MINUTES after it returns;
   *  every caller that holds a guard across an `await` must be able to end
   *  the wait itself. An aborted fetch surfaces as the ordinary
   *  `kind: "network"` result (the `catch` below), never a throw. */
  signal?: AbortSignal;
}

function classify(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 422) return "validation";
  if (status === 429) return "rate_limit";
  if (status >= 500) return "server";
  return "unknown";
}

async function readError(res: Response): Promise<ApiError> {
  let detail: unknown;
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const data = await res.json();
    detail = data;
    if (
      data &&
      typeof data === "object" &&
      "detail" in data &&
      typeof (data as { detail: unknown }).detail === "string"
    ) {
      message = (data as { detail: string }).detail;
    }
  } catch {
    /* body wasn't JSON, that's fine */
  }
  return {
    kind: classify(res.status),
    status: res.status,
    message,
    details: detail,
  };
}

// Outcome of one refresh attempt. The distinction is load-bearing: a
// transient failure (network / timeout / 5xx) must NEVER be treated like a
// rejected refresh token. Collapsing the two is what logged users out on a
// flaky connection — the once-a-day cold open hits a 401 (expired access
// token), the refresh POST fails because the network is poor, and the old
// boolean `false` made the caller `clearAll()` a perfectly valid session.
//   - "ok"        → refreshed, new tokens stored.
//   - "auth"      → the server REJECTED the refresh token (401/403); the
//                   session is genuinely dead and must be torn down.
//   - "transient" → no definitive verdict (network error / 5xx); the refresh
//                   token is very likely still valid — keep the session and
//                   let the next call retry.
type RefreshOutcome = "ok" | "auth" | "transient";

// ⚠ CYCLE 433 — ONE REFRESH IN FLIGHT PER CLIENT. `refreshOnce` is
// once-per-REQUEST, not single-flight, and always has been: every 401 that
// lands in the same moment starts its own POST to `/api/auth/refresh`.
// Production still shows the cycle-419 storm — 8 to 11 concurrent refreshes
// on an expired-token wake, when a cold open fires its whole first screen
// of requests at once and every one of them comes back 401. Each one mints
// a session row, each one rotates the refresh token, and the losers race:
// a refresh that completes after another has rotated the token can be
// rejected, and — the reason it is in THIS cycle — a refresh storm can
// cancel a reseed mid-flight, which is one of the ways a stale paint
// persists.
//
// Concurrent callers now await the SAME promise, so they see the same
// outcome and take the same branch: one "ok" and everybody retries with the
// new bearer; one "auth" and everybody tears down once; one "transient" and
// everybody backs off together instead of hammering a server that is
// already unwell.
//
// ⚠⚠ TWO THINGS MAKE SINGLE-FLIGHT SAFE, AND WITHOUT EITHER OF THEM IT IS
// WORSE THAN THE STORM IT REPLACES. Both were found by the cycle-433
// reviewers against the first version of this code and are proven red.
//
//   (a) THE SHARED REFRESH IS BOUNDED. `doRefresh`'s POST was the only
//       unbounded fetch left on the wake path — the one request sitting
//       UNDERNEATH all four reads this cycle bounded. Frozen mid-flight by
//       iOS (the documented premise of the whole cycle), it never settles,
//       so the leader's `finally` never runs, the slot is pinned for the
//       life of the page, every later 401 joins a dead promise and hangs,
//       `reseedFromRest`'s `Promise.all` never settles, the reseed runner's
//       guard never releases — and, worst, the device can no longer mint a
//       bearer, so its socket 403s and its in-app alarm rail goes silent.
//       Before single-flight a later parallel refresh could still recover
//       it. `REFRESH_TIMEOUT_MS` ends that: an abort lands in the `catch`
//       below as `"transient"`, which keeps the session, releases the slot
//       and preserves the tri-state contract exactly.
//
//   (b) THE SLOT IS SCOPED TO THE REFRESH TOKEN IT WAS STARTED WITH. This
//       SPA switches accounts WITHOUT a page reload, so module state
//       outlives an identity. An unscoped slot lets a caller under account
//       B join a refresh started under account A: A's rejection tears down
//       B's seconds-old session, or A's success writes A's tokens and B's
//       retry runs on A's bearer — B's screen rendering A's household.
//       `refreshInFlight` therefore carries the token it was minted for and
//       is joined only while that token is still the stored one; and
//       `doRefresh` re-reads the store before `setJwt`, so an orphan from a
//       previous identity can never resurrect it.
//
/** How long the shared refresh POST may take. Deliberately SHORTER than
 *  `RESEED_TIMEOUT_MS` (10 s): the reseed's four reads sit on top of this
 *  one POST, so bounding the POST first means a reseed blocked on a frozen
 *  refresh settles through its own reads rather than through its timeout.
 *  Eight seconds is generous for one POST and the verdict on expiry is
 *  `"transient"` — the session is kept and the next call retries. */
export const REFRESH_TIMEOUT_MS = 8_000;

interface RefreshSlot {
  /** The SESSION this attempt belongs to — `sub|sid` from the refresh
   *  JWT's claims. `null` when the token could not be read, which never
   *  joins. Cycle 434: this replaced the refresh token itself, which the
   *  server rotates on every call. */
  identity: string | null;
  promise: Promise<RefreshOutcome>;
}

let refreshInFlight: RefreshSlot | null = null;

/** ⚠ THE BOUND IS A RACE, NOT ONLY AN ABORT. `AbortController` is the right
 *  thing to do and it is done — but this whole cycle exists because a fetch
 *  iOS froze mid-flight did not behave, and `wakeReseed.test.ts` already
 *  records that work which ignores its abort can never be released by it.
 *  So the promise every caller awaits is the race: at `REFRESH_TIMEOUT_MS`
 *  it settles `"transient"` whatever the underlying request does, the
 *  leader's `finally` clears the slot, and no caller — and no reseed
 *  sitting on top of four of them — can be pinned past the bound. */
function bounded(
  raw: Promise<RefreshOutcome>,
  abort: () => void,
): Promise<RefreshOutcome> {
  return new Promise<RefreshOutcome>((resolve) => {
    const timer = setTimeout(() => {
      noteRefreshTimeout();
      abort();
      resolve("transient");
    }, REFRESH_TIMEOUT_MS);
    void raw.then(
      (outcome) => {
        clearTimeout(timer);
        resolve(outcome);
      },
      () => {
        clearTimeout(timer);
        resolve("transient");
      },
    );
  });
}

/** The stored refresh token, or `null`. ⚠ Never throws: `getJwt()` reads
 *  storage, and a storage subsystem that throws would otherwise reject out
 *  of the async `refreshOnce` below.
 *
 *  ⚠ STATED NARROWLY, because the obvious wider claim is false: this does
 *  NOT make `request()` throw-proof. `getBearer()` and `getDeviceToken()`
 *  are still called unguarded earlier in `request()` and `authedFetch`, and
 *  `auth.ts`'s `readToken` re-reads `localStorage` outside its own catch —
 *  so under genuinely throwing storage the request rejects before ever
 *  reaching here. That is pre-existing and out of this cycle's surface. It
 *  is also why failing to `null` here is harmless: a state in which this
 *  helper's catch fires is a state in which the caller never arrived. */
function storedRefreshToken(): string | null {
  try {
    return getJwt()?.refresh ?? null;
  } catch {
    return null;
  }
}

/** ⚠ CYCLE 434 — THE SLOT IS KEYED ON THE SESSION, NOT ON THE TOKEN.
 *
 *  Cycle 433 keyed it on the stored refresh token, and the join branch then
 *  never once executed in production: `POST /api/auth/refresh` **rotates
 *  the refresh token on every call** — the server's own words are "rotate
 *  WITHIN it, same row, new pair" — so the first refresh in a storm
 *  succeeded, `setJwt` stored a different token, and every caller arriving
 *  after that compared against a value that no longer matched the slot and
 *  started its own. The join window was "before the first refresh
 *  completes", i.e. approximately zero. Five hours of production: three
 *  bursts, one of fourteen concurrent refreshes, and `refresh_coalesced`
 *  never non-zero.
 *
 *  The identity here is `sub` (the account) and `sid` (the SESSION row)
 *  read from the refresh JWT's own claims. **Neither can rotate mid-storm,
 *  and that is a property of the server, not an assumption:** a rotation
 *  reuses the same session row and re-issues the same `sid` with the same
 *  `sub`; only `jti` and the token string change. A sign-out plus sign-in
 *  mints a NEW session row, so `sid` changes and the slot is correctly not
 *  joined — which is the security property from cycle 433's HIGH-1, now
 *  enforced on something stable instead of something that churns.
 *
 *  ⚠ BOTH fields must match, and an unreadable token NEVER joins. Failing
 *  to "start my own refresh" costs one redundant request; failing the other
 *  way would let one account's refresh be joined by another's. */
export function refreshIdentity(token: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const p = JSON.parse(atob(padded)) as { sub?: unknown; sid?: unknown };
    const sub = typeof p.sub === "string" && p.sub ? p.sub : null;
    if (!sub) return null; // no account on it — never join
    // A pre-419 token carries no `sid`; the server mints a session row for
    // it on first refresh. Keying such a token on its account alone is
    // still correct for the security property (one ACCOUNT's refresh is
    // never joined by another's) and it self-heals on the next rotation.
    const sid = typeof p.sid === "string" && p.sid ? p.sid : "nosid";
    return sub + "|" + sid;
  } catch {
    return null; // unparseable — never join
  }
}

/**
 * ⚠ Cycle 435 (external review): THE SESSION THIS REQUEST WAS SENT UNDER.
 *
 * `/login` is routed inside the mounted shell, so signing in as a different
 * account swaps the credential while requests are still in flight. The 401
 * handling below reads whatever is in storage AT THE MOMENT THE 401 IS
 * HANDLED — so the previous account's rejection could refresh, retry, or
 * (the sharp one) TEAR DOWN the account that just signed in, ending its
 * Live Activity and clearing its credentials while its alarm rail was the
 * one on screen.
 *
 * The guard is the same rule this cycle applied to the socket reseed, the
 * member identity and the settings cache: capture the identity when the
 * work STARTS and drop the answer if it changed. It is one-directional —
 * skipping the 401 dance can only leave a session signed IN.
 *
 * A token ROTATION is not a change: the server reuses the session row and
 * re-issues the same `sub` and `sid`, so the identity is stable across it
 * and the ordinary refresh path is untouched. A sign-out and back in mints
 * a new `sid`, which is correctly a change.
 */
function sessionIdentity(token: string | null): string | null {
  return refreshIdentity(token);
}

async function refreshOnce(): Promise<RefreshOutcome> {
  const current = storedRefreshToken();
  if (current == null) return "auth"; // nothing to refresh with
  const identity = refreshIdentity(current);
  if (
    refreshInFlight != null &&
    identity != null &&
    refreshInFlight.identity === identity
  ) {
    noteRefreshCoalesced();
    return refreshInFlight.promise;
  }
  // ⚠ Cycle 434 diagnostic: this load is STARTING a refresh, not joining
  // one. `refresh_starts` against `refresh_coalesced`, both reported beside
  // a per-page-load id, is what will say whether the 07:49 burst was one
  // context whose slot failed or fourteen contexts each doing one.
  noteRefreshStart();
  const ctrl = new AbortController();
  const slot: RefreshSlot = {
    identity,
    promise: bounded(doRefresh(current, ctrl.signal), () => ctrl.abort()),
  };
  refreshInFlight = slot;
  try {
    return await slot.promise;
  } finally {
    // ⚠ In a `finally`, so a rejection — which `doRefresh` does not produce
    // today but a future edit could — can never leave the slot pinned and
    // every later refresh awaiting a dead promise. Only OUR slot is
    // cleared: a fresher attempt started under a new identity keeps its.
    if (refreshInFlight === slot) refreshInFlight = null;
  }
}

/** Test seam: forget any in-flight refresh. Does NOT abort the attempt —
 *  it does not need to, because the attempt is bounded by
 *  `REFRESH_TIMEOUT_MS` and because `doRefresh` refuses to write tokens
 *  for a refresh token that is no longer the stored one. */
export function resetRefreshSingleFlight(): void {
  refreshInFlight = null;
}

/** ⚠ "IS THE SESSION IN STORAGE STILL THE ONE THIS ATTEMPT WAS STARTED
 *  FOR?" — the single guard both `doRefresh` branches ask, so they cannot
 *  drift apart.
 *
 *  Normally that is an identity comparison: `sub|sid`, which survives the
 *  server rotating the token on every call. But `refreshIdentity` returns
 *  `null` for anything it cannot decode, and a bare `a !== b` treats two
 *  UNREADABLE tokens as EQUAL (`null !== null` is false) — so the guard
 *  would wave a write through exactly when it understands least. That hole
 *  was found by the cycle-434 security review.
 *
 *  ⚠ THE FIX IS NOT TO REFUSE THE WRITE. Failing closed on a null identity
 *  reads well and is the wrong trade for this platform: a 200 whose token
 *  we cannot decode means the SERVER accepted a credential our local
 *  `atob` could not read, and refusing to store the fresh pair leaves the
 *  device unable to mint a bearer for the rest of its life — a dark alarm
 *  surface, which is the harm that outranks everything here. So when
 *  either side is unreadable we fall back to comparing the RAW TOKENS,
 *  which is precisely cycle 433's comparison and has no decode dependency
 *  at all. Strictly better than `null !== null`: two different corrupt
 *  tokens now compare as different, and the repair path stays open. */
function sameSessionAsSent(
  sentToken: string,
  sentIdentity: string | null,
): boolean {
  const stored = storedRefreshToken();
  const storedIdentity = refreshIdentity(stored);
  if (sentIdentity != null && storedIdentity != null) {
    return sentIdentity === storedIdentity;
  }
  return stored === sentToken;
}

async function doRefresh(
  sentToken: string,
  signal: AbortSignal,
): Promise<RefreshOutcome> {
  const jwt = getJwt();
  if (!jwt) return "auth"; // nothing to refresh with → genuinely signed out
  // Captured BEFORE the await, so the two guards below compare against the
  // session this attempt was started for, not whatever is stored later.
  const sentIdentity = refreshIdentity(sentToken);
  try {
    const res = await fetch(apiUrl("/api/auth/refresh"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt.refresh}`,
      },
      body: JSON.stringify({ refresh_token: jwt.refresh }),
      // ⚠ `AbortController` rather than `AbortSignal.timeout`, which is
      // newer than the fleet's kiosk shell; this is the pattern
      // `api/voice.ts` already ships. The DEADLINE lives in `bounded`.
      signal,
    });
    if (res.ok) {
      const data = (await res.json()) as {
        access_token: string;
        refresh_token: string;
      };
      // ⚠ THE IDENTITY CHECK — CYCLE 434: ON THE SESSION, NOT THE TOKEN.
      // If the stored token now belongs to a DIFFERENT session, the one
      // this attempt was started for is gone (signed out, switched), and
      // writing here would resurrect a dead identity over a live one. Say
      // "transient": the session in place is kept and the caller retries.
      // ⚠ Comparing the raw token here would now be wrong as well as
      // strict: the server rotates it on every call, so "the token moved"
      // no longer means "the identity moved".
      if (!sameSessionAsSent(sentToken, sentIdentity)) {
        return "transient";
      }
      setJwt(data);
      return "ok";
    }
    // Only a 401/403 means the refresh token itself is dead. Any other
    // status (5xx, gateway blip) is a server-side hiccup that must NOT
    // wipe a valid session.
    if (res.status === 401 || res.status === 403) {
      // ⚠ THE IDENTITY CHECK BELONGS ON THIS BRANCH TOO (cycle 433's
      // MEDIUM-A). A stale rejection is the more dangerous half: sign out
      // of A (which revokes A's session server-side), sign back in, and A's
      // in-flight refresh returns 401 `session_revoked` — whereupon an
      // unscoped `"auth"` tears down and server-revokes B's seconds-old
      // session, taking that device's in-app alarm rail with it. A
      // rejection only means anything about the session it was sent for.
      if (!sameSessionAsSent(sentToken, sentIdentity)) {
        return "transient";
      }
      return "auth";
    }
    return "transient";
  } catch {
    // Network error / timeout / DNS / TLS / OUR OWN ABORT — the request
    // never reached a verdict. Keep the session; the next call (better
    // connectivity) will refresh cleanly.
    return "transient";
  }
}

/** Lower-level: same bearer + 401-refresh behavior as `request()` but
 *  returns the raw `Response` so callers can stream / blob the body
 *  themselves (CSV download, etc.). */
/**
 * ⚠ Cycle 418: the genuine-auth-rejection teardown, imported LAZILY.
 *
 * `accountTeardown` reaches `api/devices`, which imports this module — a
 * static import here would be a load-time cycle. It is also NOT `explicit`:
 * a revoked device token must still land a kiosk on `/pair` (cycle 358). A
 * teardown that itself fails must never mask the original 401, so the whole
 * thing is swallowed and `clearAll()` remains the guaranteed floor.
 */
async function teardownAfterAuthRejection(): Promise<void> {
  try {
    const m = await import("./accountTeardown");
    await m.teardownAccount({ explicit: false });
  } catch {
    clearAll();
  }
}

export async function authedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  const bearer = getBearer();
  if (bearer && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${bearer}`);
  }
  for (const [k, v] of Object.entries(clientIdHeader())) {
    if (!headers.has(k)) headers.set(k, v);
  }
  const merged: RequestInit = { ...init, headers };

  const sentSession = sessionIdentity(bearer);

  let res = await fetch(apiUrl(path), merged);
  if (res.status === 401 && getDeviceToken() === null) {
    if (sessionIdentity(getBearer()) !== sentSession) {
      // A different session signed in while this request was in flight.
      // This 401 belongs to the PREVIOUS one; never refresh, retry or tear
      // down the current session on its behalf. Return the 401 as-is.
      return res;
    }
    const outcome = await refreshOnce();
    if (outcome === "ok") {
      const next = new Headers(init.headers);
      const fresh = getBearer();
      if (fresh) next.set("Authorization", `Bearer ${fresh}`);
      res = await fetch(apiUrl(path), { ...init, headers: next });
    } else if (outcome === "auth") {
      await teardownAfterAuthRejection();
    }
    // "transient": leave the session intact and return the original 401 —
    // the refresh couldn't complete, but the token may be fine. Do NOT
    // clearAll on a network/5xx hiccup.
  }
  return res;
}

export async function request<T>(
  path: string,
  opts: RequestOpts = {},
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
    // Cycle 429: the install id rides AUTHENTICATED requests only — the
    // door line is the sole reader and it sits behind a credential. A
    // login, signup or reset call carries no install id (security review:
    // a choice, not an accident).
    ...(opts.noAuth ? {} : clientIdHeader()),
    ...(opts.headers ?? {}),
  };
  const bearer = opts.bearer ?? (opts.noAuth ? null : getBearer());
  if (bearer) headers["Authorization"] = `Bearer ${bearer}`;
  const sentSession = sessionIdentity(bearer);

  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (opts.signal) init.signal = opts.signal;
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  let res: Response;
  try {
    res = await fetch(apiUrl(path), init);
  } catch (e) {
    return {
      ok: false,
      error: {
        kind: "network",
        status: 0,
        message: e instanceof Error ? e.message : "network error",
      },
    };
  }

  // 401 dance: only worth retrying if we sent a JWT (device tokens have
  // no refresh path). Tear the session down ONLY when the refresh token is
  // actually rejected — a transient refresh failure (network/5xx) bubbles a
  // retryable network error and leaves the tokens in place.
  if (res.status === 401 && !opts.noRetry && !opts.noAuth) {
    if (sessionIdentity(getBearer()) !== sentSession) {
      // See `sessionIdentity`: a stale caller's rejection never acts on the
      // session that replaced it.
      return { ok: false, error: await readError(res) };
    }
    const sentDeviceToken = getDeviceToken() !== null;
    if (!sentDeviceToken) {
      const outcome = await refreshOnce();
      if (outcome === "ok") return request<T>(path, { ...opts, noRetry: true });
      if (outcome === "transient") {
        // Couldn't reach a refresh verdict. Keep the session; surface a
        // retryable network error so the cycle-76d resilient loaders retry
        // instead of bouncing the user to the login screen.
        return {
          ok: false,
          error: {
            kind: "network",
            status: 0,
            message: "Couldn't reach the server to refresh the session.",
          },
        };
      }
    }
    await teardownAfterAuthRejection();
    return { ok: false, error: await readError(res) };
  }

  if (!res.ok) return { ok: false, error: await readError(res) };

  // 204 / empty body
  if (res.status === 204) return { ok: true, data: undefined as unknown as T };
  const text = await res.text();
  if (!text) return { ok: true, data: undefined as unknown as T };
  try {
    return { ok: true, data: JSON.parse(text) as T };
  } catch {
    // CSV / non-JSON success — return raw text cast to T (rare path).
    return { ok: true, data: text as unknown as T };
  }
}
